/**
 * Novo lançamento (e edição do lançamento manual).
 *
 * O texto desta tela é do dono do produto e está reproduzido em
 * `docs/plano/lancamento-financeiro.md`; ele vai como está. Três coisas
 * carregam o comportamento:
 *
 * 1. **"Valor líquido" é conta, não campo.** Exibido, nunca digitado —
 *    `bruto − desconto + acréscimo`. Soma e subtração de inteiros é o que o
 *    front pode fazer; o que a CONSTITUICAO.md §1 proíbe é DIVIDIR fora de
 *    `Ratear`, e é por isso que o rateio das parcelas continua no servidor: a
 *    tela promete a divisão, não a desenha.
 *
 * 2. **O rodapé lista pelo NOME o que falta.** "Lançar título" apagado e sem
 *    explicação faz a pessoa procurar o campo errado num formulário de seis
 *    grupos. A lista vem de `faltaParaSalvar`, que é pura e testável.
 *
 * 3. **"Já está recebido" é a única situação em que CRIAR mexe em saldo.** O
 *    texto diz isso e a tela repete — um switch cujo efeito colateral só
 *    aparece no extrato é a definição de armadilha. Com parcelamento ele fica
 *    desligado e desabilitado: parcela já liquidada não existe.
 *
 * Uma divergência registrada: o texto do tipo Despesa promete "centro de custo
 * e código de barras", que não têm coluna no banco. A frase foi ajustada para
 * não prometer campo que a tela não tem — ver `docs/de-para.md`.
 */
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react'

import { Pill } from '../../componentes/Basicos'
import { Campo, Chave, MensagemDeErro } from '../../componentes/Campos'
import { CampoData } from '../../componentes/CampoData'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { analisar, formatar, formatarMoeda } from '../../plataforma/dinheiro'
import {
  carregarCategorias,
  carregarContasFinanceiras,
  carregarProximoDocumento,
  faltaParaSalvar,
  hojeISO,
  ladoDoTipo,
  lancarConta,
  liquidoDe,
  salvarConta,
  tipoDoLado,
  FORMAS,
  type CategoriaFinanceira,
  type ContaFinanceira,
  type FormaConta,
  type Lado,
  type LinhaConta,
  type PessoaResumo,
  type TipoLancamento,
} from '../../plataforma/financeiro'
import { dataISOparaBR } from '../../plataforma/mascaras'
import { BuscaDePessoa } from './BuscaDePessoa'
import { Botao } from '../../componentes/Botao'

const ROTULO_GRUPO = 'text-[11px] font-bold tracking-[0.6px] uppercase text-texto-suave'

/** As parcelas que o seletor oferece. */
const PARCELAS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

export function FormularioDeLancamento({
  ladoInicial,
  edicao,
  aoFechar,
  aoConcluir,
}: {
  ladoInicial: Lado
  /** Quando presente, é edição de um título manual em aberto. */
  edicao?: LinhaConta
  aoFechar: () => void
  aoConcluir: (aviso: string, lado: Lado) => void
}) {
  const editando = !!edicao
  const idObservacao = useId()
  const idCategoria = useId()
  const idForma = useId()
  const idConta = useId()
  const idParcelas = useId()

  const [tipo, setTipo] = useState<TipoLancamento>(tipoDoLado(ladoInicial))
  const [numeroDocumento, setNumeroDocumento] = useState(edicao?.numero_documento ?? '')
  const [documentoTocado, setDocumentoTocado] = useState(editando)
  const [pessoa, setPessoa] = useState<PessoaResumo | null>(
    edicao ? (edicao.cliente ?? edicao.fornecedor ?? null) : null,
  )
  const [categoria, setCategoria] = useState(edicao?.categoria?.codigo ?? '')
  const [descricao, setDescricao] = useState(edicao?.descricao ?? '')
  const [valorBruto, setValorBruto] = useState(
    edicao ? formatar(edicao.valor_bruto_centavos) : '',
  )
  const [desconto, setDesconto] = useState(
    edicao && edicao.desconto_centavos > 0 ? formatar(edicao.desconto_centavos) : '',
  )
  const [acrescimo, setAcrescimo] = useState(
    edicao && edicao.acrescimo_centavos > 0 ? formatar(edicao.acrescimo_centavos) : '',
  )
  const [emissao, setEmissao] = useState(edicao?.emissao ?? hojeISO())
  const [vencimento, setVencimento] = useState(edicao?.vencimento ?? '')
  const [parcelada, setParcelada] = useState(false)
  const [parcelas, setParcelas] = useState(2)
  /*
   * "Dinheiro" é o padrão porque é o que o balcão usa quando ninguém escolhe —
   * e porque o rodapé lista o que falta pelo nome: um campo obrigatório sem
   * padrão apareceria na lista antes de a pessoa chegar nele.
   */
  const [forma, setForma] = useState<FormaConta | ''>(edicao?.forma ?? 'dinheiro')
  const [contaFinanceira, setContaFinanceira] = useState(edicao?.conta_financeira?.codigo ?? '')
  const [jaLiquidada, setJaLiquidada] = useState(false)
  const [observacao, setObservacao] = useState(edicao?.observacao ?? '')

  const [categorias, setCategorias] = useState<CategoriaFinanceira[]>([])
  const [contas, setContas] = useState<ContaFinanceira[]>([])
  const [erro, setErro] = useState('')
  const [errosCampo, setErrosCampo] = useState<Record<string, string>>({})
  const [salvando, setSalvando] = useState(false)

  const lado = ladoDoTipo(tipo)

  /* As categorias são filtradas por tipo — é o que cumpre "só categorias de
     receita aparecem aqui". Trocar o tipo invalida a escolha anterior. */
  useEffect(() => {
    const controle = new AbortController()
    carregarCategorias(tipo, controle.signal)
      .then((dados) => setCategorias(dados.categorias ?? []))
      .catch(() => setCategorias([]))
    return () => controle.abort()
  }, [tipo])

  useEffect(() => {
    const controle = new AbortController()
    carregarContasFinanceiras(controle.signal)
      .then((dados) => setContas(dados.contas ?? []))
      .catch(() => {
        // "Conta prevista" é opcional: sem a lista, o campo some do caminho e
        // a baixa decide depois. Não é motivo para travar o lançamento.
      })
    return () => controle.abort()
  }, [])

  /* O número sugerido só é buscado enquanto a pessoa não mexeu no campo — o
     contrato diz "sugerido pelo sistema, editável", e sobrescrever o que ela
     digitou ao trocar o tipo apagaria a escolha dela. */
  useEffect(() => {
    if (documentoTocado) return
    const controle = new AbortController()
    carregarProximoDocumento(lado, controle.signal)
      .then((dados) => setNumeroDocumento(dados.numero_documento))
      .catch(() => {
        // Sem sugestão, o campo continua obrigatório e o rodapé cobra por nome.
      })
    return () => controle.abort()
  }, [lado, documentoTocado])

  /* Trocar de tipo troca de cadastro e de plano de contas: manter cliente e
     categoria de receita num lançamento de despesa é o caminho para um 422. */
  useEffect(() => {
    if (editando) return
    setPessoa(null)
    setCategoria('')
  }, [tipo, editando])

  const brutoCentavos = analisar(valorBruto)
  const descontoCentavos = desconto.trim() === '' ? 0 : analisar(desconto)
  const acrescimoCentavos = acrescimo.trim() === '' ? 0 : analisar(acrescimo)
  const liquidoCentavos =
    brutoCentavos === null || descontoCentavos === null || acrescimoCentavos === null
      ? null
      : liquidoDe(brutoCentavos, descontoCentavos, acrescimoCentavos)

  const falta = faltaParaSalvar({
    tipo,
    numero_documento: numeroDocumento,
    pessoa: pessoa?.codigo ?? '',
    categoria,
    descricao,
    valor_bruto_centavos: brutoCentavos,
    emissao,
    vencimento,
    forma,
  })

  const liquidoInvalido = liquidoCentavos !== null && liquidoCentavos <= 0
  const podeSalvar = falta.length === 0 && !liquidoInvalido && !salvando

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (!podeSalvar || brutoCentavos === null || forma === '') return

    setErro('')
    setErrosCampo({})
    setSalvando(true)

    const pedido = {
      numero_documento: numeroDocumento.trim(),
      pessoa: pessoa?.codigo,
      categoria,
      descricao: descricao.trim(),
      valor_bruto_centavos: brutoCentavos,
      desconto_centavos: descontoCentavos ?? 0,
      acrescimo_centavos: acrescimoCentavos ?? 0,
      emissao,
      vencimento,
      parcelas: parcelada && !editando ? parcelas : 1,
      forma,
      conta_financeira: contaFinanceira || undefined,
      ja_liquidada: jaLiquidada && !parcelada && !editando,
      observacao: observacao.trim() || undefined,
    }

    try {
      if (edicao) {
        await salvarConta(lado, edicao.codigo, pedido)
        aoConcluir(`Título ${pedido.numero_documento} salvo.`, lado)
        return
      }

      const resultado = await lancarConta(lado, pedido)
      /* A contagem é do SERVIDOR (`parcelas`), não do que a tela pediu: se o
         rateio produzir número diferente, é o dele que vale. As duas quedas
         são defesa, não decisão — uma resposta abreviada não pode derrubar a
         tela depois de o título já ter sido gravado. */
      const geradas = resultado.parcelas ?? resultado.contas?.length ?? pedido.parcelas
      aoConcluir(avisoDoLancamento(geradas, pedido.numero_documento, vencimento, pedido.ja_liquidada, lado), lado)
    } catch (causa: unknown) {
      if (causa instanceof ErroApi) {
        setErrosCampo(causa.campos)
        setErro(causa.message)
      } else {
        setErro('Não foi possível lançar o título.')
      }
      setSalvando(false)
    }
  }

  return (
    // Sem cabeçalho próprio: título, subtítulo e trilha são do `LayoutApp`
    // agora que isto é uma TELA. Enquanto morava na gaveta, o formulário
    // precisava desenhar o próprio topo e o próprio X.
    // `aria-label` não é enfeite: um `<form>` sem nome acessível não tem
    // `role="form"` para o leitor de tela — ele vira uma `<div>` qualquer, e
    // some da lista de regiões da página.
    <form
      onSubmit={enviar}
      noValidate
      aria-label={editando ? 'Editar lançamento' : 'Novo lançamento'}
      className="flex w-full flex-col"
    >
      <div className="flex flex-col gap-4">
        {/* ── o que é este lançamento ── */}
        <Grupo
          numero="01"
          titulo="O que é este lançamento"
          /* A frase do produto termina em "…despesa pede fornecedor, centro de
             custo e código de barras". Centro de custo e código de barras não
             existem no banco desta rodada — a promessa foi cortada em vez de
             virar campo falso. */
          ajuda="O tipo decide o resto da tela: receita pede cliente, despesa pede fornecedor."
        >
          <div role="group" aria-label="Tipo do lançamento" className="flex flex-wrap gap-2">
            <Pill
              ativa={tipo === 'receita'}
              desabilitada={editando}
              aoClicar={() => setTipo('receita')}
            >
              Receita — vou receber
            </Pill>
            <Pill
              ativa={tipo === 'despesa'}
              desabilitada={editando}
              aoClicar={() => setTipo('despesa')}
            >
              Despesa — vou pagar
            </Pill>
          </div>
          {editando && (
            <p className="mt-2 text-[12.5px] text-texto-suave">
              O tipo não muda depois de lançado — um título não troca de lado.
            </p>
          )}
        </Grupo>

        {/* ── de quem, e de quê ── */}
        <Grupo numero="02" titulo="De quem, e de quê">
          <div className="flex flex-col gap-4">
            <BuscaDePessoa
              tipo={tipo}
              escolhida={pessoa}
              aoEscolher={setPessoa}
              erro={errosCampo.cliente ?? errosCampo.fornecedor}
            />

            <div>
              <label
                htmlFor={idCategoria}
                className="mb-2 block text-[13px] font-semibold text-texto"
              >
                Categoria *
              </label>
              <select
                id={idCategoria}
                value={categoria}
                onChange={(evento) => setCategoria(evento.target.value)}
                aria-invalid={errosCampo.categoria ? true : undefined}
                className="campo rounded-sistema h-[38px]"
              >
                <option value="">Escolha a categoria</option>
                {categorias.map((item) => (
                  <option key={item.codigo} value={item.codigo}>
                    {item.nome}
                  </option>
                ))}
              </select>
              {errosCampo.categoria ? (
                <MensagemDeErro>{errosCampo.categoria}</MensagemDeErro>
              ) : (
                <p className="mt-1.5 text-[12.5px] text-texto-fraco">
                  Só categorias de {tipo} aparecem aqui
                  {categorias.length === 0 && ' — nenhuma cadastrada ainda'}
                </p>
              )}
            </div>

            <Campo
              rotulo="Descrição *"
              value={descricao}
              onChange={(evento) => setDescricao(evento.target.value)}
              erro={errosCampo.descricao}
              dica="É o que vai aparecer na listagem e no recibo"
              maxLength={200}
            />

            <Campo
              rotulo="Número do documento *"
              value={numeroDocumento}
              onChange={(evento) => {
                setDocumentoTocado(true)
                setNumeroDocumento(evento.target.value)
              }}
              erro={errosCampo.numero_documento}
              dica="Sugerido pelo sistema. Precisa ser único."
            />
          </div>
        </Grupo>

        {/* ── quanto ── */}
        <Grupo numero="03" titulo="Quanto">
          <div className="grid gap-4 sm:grid-cols-3">
            <Campo
              rotulo="Valor bruto *"
              value={valorBruto}
              onChange={(evento) => setValorBruto(evento.target.value)}
              erro={errosCampo.valor_bruto_centavos}
              inputMode="decimal"
              placeholder="0,00"
            />
            <Campo
              rotulo="Desconto"
              value={desconto}
              onChange={(evento) => setDesconto(evento.target.value)}
              erro={errosCampo.desconto_centavos}
              inputMode="decimal"
              placeholder="0,00"
            />
            <Campo
              rotulo="Acréscimo"
              value={acrescimo}
              onChange={(evento) => setAcrescimo(evento.target.value)}
              erro={errosCampo.acrescimo_centavos}
              inputMode="decimal"
              placeholder="0,00"
            />
          </div>

          {/* Exibido, nunca digitado: é conta, não campo. */}
          <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3 rounded-sistema border border-borda bg-fundo px-4 py-3">
            <div>
              <p className={ROTULO_GRUPO}>Valor líquido</p>
              <p className="mt-0.5 text-[12.5px] text-texto-suave">
                Bruto − desconto + acréscimo. É conta, não campo.
              </p>
            </div>
            <p
              className={`tabular font-titulo text-[22px] font-bold ${
                liquidoInvalido ? 'text-erro' : 'text-titulo'
              }`}
            >
              {liquidoCentavos === null ? '—' : formatarMoeda(liquidoCentavos)}
            </p>
          </div>
          {liquidoInvalido && (
            <MensagemDeErro>
              O desconto não pode zerar nem inverter o título — o líquido precisa ser maior que
              zero.
            </MensagemDeErro>
          )}
        </Grupo>

        {/* ── quando ── */}
        <Grupo numero="04" titulo="Quando">
          <div className="grid gap-4 sm:grid-cols-2">
            <CampoData
              rotulo="Emissão *"
              value={emissao}
              onChange={setEmissao}
              erro={errosCampo.emissao}
            />
            <CampoData
              rotulo={parcelada ? 'Vencimento da 1ª parcela *' : 'Vencimento *'}
              value={vencimento}
              onChange={setVencimento}
              erro={errosCampo.vencimento}
            />
          </div>

          {editando ? (
            <p className="mt-4 text-[12.5px] text-texto-suave">
              {edicao && edicao.total_parcelas > 1
                ? `Parcela ${edicao.numero_parcela} de ${edicao.total_parcelas}. A edição vale só para esta parcela.`
                : 'Ocorrência única.'}
            </p>
          ) : (
            <div className="mt-4">
              <p className="mb-2 text-[13px] font-semibold text-texto">Ocorrência</p>
              <div role="group" aria-label="Ocorrência" className="flex flex-wrap gap-2">
                <Pill
                  ativa={!parcelada}
                  aoClicar={() => {
                    setParcelada(false)
                  }}
                >
                  Única
                </Pill>
                <Pill
                  ativa={parcelada}
                  aoClicar={() => {
                    setParcelada(true)
                    // Parcelamento e "já liquidado" não convivem: o servidor
                    // recusa, e desligar aqui evita descobrir isso no 422.
                    setJaLiquidada(false)
                  }}
                >
                  Parcelada
                </Pill>
              </div>

              {parcelada && (
                <div className="mt-3">
                  <label
                    htmlFor={idParcelas}
                    className="mb-2 block text-[13px] font-semibold text-texto"
                  >
                    Parcelas
                  </label>
                  <select
                    id={idParcelas}
                    value={parcelas}
                    onChange={(evento) => setParcelas(Number(evento.target.value))}
                    className="campo rounded-sistema h-[38px]"
                  >
                    {PARCELAS.map((n) => (
                      <option key={n} value={n}>
                        {n} parcelas mensais
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 flex items-start gap-2 rounded-sistema bg-marca-fundo-2 px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-marca-forte">
                    <Icone nome="info" tamanho={16} className="mt-px shrink-0" />
                    <span>
                      {liquidoCentavos !== null && liquidoCentavos > 0 && (
                        <>
                          <span className="tabular">{formatarMoeda(liquidoCentavos)}</span> em{' '}
                        </>
                      )}
                      <span className="tabular">{parcelas}</span> parcelas mensais
                      {vencimento && (
                        <>
                          {' '}
                          a partir de <span className="tabular">{dataISOparaBR(vencimento)}</span>
                        </>
                      )}
                      . Quem divide é o servidor: o centavo que sobra vai para as primeiras
                      parcelas, e a soma delas fecha exatamente com o líquido.
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </Grupo>

        {/* ── como o dinheiro entra ── */}
        <Grupo
          numero="05"
          titulo={tipo === 'receita' ? 'Como o dinheiro entra' : 'Como o dinheiro sai'}
        >
          <div className="flex flex-col gap-4">
            <div>
              <label htmlFor={idForma} className="mb-2 block text-[13px] font-semibold text-texto">
                Forma de pagamento *
              </label>
              <select
                id={idForma}
                value={forma}
                onChange={(evento) => setForma(evento.target.value as FormaConta | '')}
                aria-invalid={errosCampo.forma ? true : undefined}
                className="campo rounded-sistema h-[38px]"
              >
                <option value="">Escolha a forma</option>
                {FORMAS.map((item) => (
                  <option key={item.valor} value={item.valor}>
                    {item.rotulo}
                  </option>
                ))}
              </select>
              {errosCampo.forma && <MensagemDeErro>{errosCampo.forma}</MensagemDeErro>}
            </div>

            <div>
              <label htmlFor={idConta} className="mb-2 block text-[13px] font-semibold text-texto">
                Conta prevista
              </label>
              <select
                id={idConta}
                value={contaFinanceira}
                onChange={(evento) => setContaFinanceira(evento.target.value)}
                className="campo rounded-sistema h-[38px]"
              >
                <option value="">Definir na baixa</option>
                {contas.map((item) => (
                  <option key={item.codigo} value={item.codigo}>
                    {item.nome}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-[12.5px] text-texto-fraco">
                Definir na baixa. Opcional. O saldo só se move na baixa, não agora.
              </p>
            </div>

            {!editando && (
              <Chave
                ligada={jaLiquidada}
                desabilitada={parcelada}
                aoMudar={setJaLiquidada}
                rotulo={tipo === 'receita' ? 'Já está recebido' : 'Já está pago'}
                ajuda={
                  parcelada
                    ? 'Indisponível no parcelamento: parcela já liquidada não existe. Volte para “Única” se o dinheiro já entrou.'
                    : `Ligue só se o dinheiro já ${tipo === 'receita' ? 'entrou' : 'saiu'}. O lançamento nasce liquidado e o saldo da conta se move na hora — é a única situação em que criar mexe em saldo.`
                }
              />
            )}
          </div>
        </Grupo>

        {/* ── observações ── */}
        <Grupo numero="06" titulo="Observações">
          <label
            htmlFor={idObservacao}
            className="mb-2 block text-[13px] font-semibold text-texto"
          >
            Observação
          </label>
          <textarea
            id={idObservacao}
            value={observacao}
            onChange={(evento) => setObservacao(evento.target.value)}
            rows={3}
            maxLength={500}
            aria-invalid={errosCampo.observacao ? true : undefined}
            className="campo rounded-sistema py-2.5"
          />
          {errosCampo.observacao ? (
            <MensagemDeErro>{errosCampo.observacao}</MensagemDeErro>
          ) : (
            <p className="mt-1.5 text-[12.5px] text-texto-fraco">
              Opcional. Aparece na gaveta do lançamento.
            </p>
          )}
        </Grupo>

        {erro && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] font-medium text-erro-texto"
          >
            <Icone nome="error" tamanho={16} className="mt-px shrink-0" />
            <span>{erro}</span>
          </p>
        )}
      </div>

      {/* ── rodapé: o que falta, pelo nome ── */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-borda pt-4">
        <p className="max-w-[44ch] text-[12.5px] leading-[1.5] text-texto-suave">
          {falta.length > 0
            ? `Para salvar, faltam: ${falta.join(', ')}.`
            : jaLiquidada
              ? `Nasce liquidado — o saldo se move agora, não na baixa.`
              : 'Tudo preenchido.'}
        </p>
        <div className="flex shrink-0 items-center gap-2.5">
          <Botao variante="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao type="submit" disabled={!podeSalvar}>
            {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Lançar título'}
          </Botao>
        </div>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------------ peças */

/**
 * Um bloco do formulário, no CARTÃO NUMERADO do sistema.
 *
 * Era um rótulo em versalete solto sobre o fundo da página, com os campos
 * embaixo. Funcionava dentro da gaveta, onde a moldura branca já vinha de
 * fora; na tela, os seis grupos viravam uma coluna contínua de campos sem
 * nenhuma separação visual — e a tela ficava sozinha, com uma forma que
 * nenhuma outra do sistema usa.
 *
 * Agora é a mesma forma de "Dados da empresa" e "Meu perfil": cartão branco,
 * número, título e uma frase de apoio quando ela acrescenta algo.
 */
function Grupo({
  numero,
  titulo,
  ajuda,
  children,
}: {
  numero: string
  titulo: string
  ajuda?: string
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-4">
        <h3 className="font-titulo text-base font-semibold text-tinta">
          <span className="tabular text-marca-forte">{numero}</span>
          <span aria-hidden className="mx-1.5 text-inativo">
            ·
          </span>
          {titulo}
        </h3>
        {ajuda && <p className="mt-[2px] text-[12.5px] leading-[1.5] text-texto-suave">{ajuda}</p>}
      </div>
      {children}
    </section>
  )
}

/** O aviso que a listagem mostra depois de gravar. */
function avisoDoLancamento(
  geradas: number,
  documento: string,
  vencimento: string,
  jaLiquidada: boolean,
  lado: Lado,
): string {
  if (geradas > 1) {
    return `${geradas} parcelas lançadas em ${documento}, a primeira vence em ${dataISOparaBR(vencimento)}.`
  }
  if (jaLiquidada) {
    return `Título ${documento} lançado e já ${lado === 'receber' ? 'recebido' : 'pago'}.`
  }
  return `Título ${documento} lançado, com vencimento em ${dataISOparaBR(vencimento)}.`
}
